import { baseApi } from './baseApi';

export interface CreditTransactionPreview {
  _id: string;
  amount: number;
  kind: 'consume' | 'grant' | 'refund' | 'debit_adjustment' | 'purchase' | 'distribution';
  action?: string;
  consumerModule?: string;
  balanceAfter: number;
  reason?: string;
  performedBy?: string;
  createdAt: string;
  /** Acciones (tools) que ejecutó Gustavo en ese mensaje. */
  actions?: string[];
}

export interface CreditsMe {
  systemEnabled: boolean;  // master switch del sistema (flag 'credits')
  /**
   * De quién es la bolsa que devuelve el endpoint:
   * - 'user'   → wallet propio del usuario (Imperium: 300/mes por agente).
   * - 'tenant' → pool de Gustavo de la ORGANIZACIÓN, del plan (tenants Booster). El saldo
   *   y el consumido son los de toda la inmobiliaria — los mismos que muestra el tab
   *   Suscripción. Los movimientos, en cambio, siguen siendo los del usuario.
   */
  scope: 'user' | 'tenant';
  balance: number;
  totalEarned: number;
  totalConsumed: number;
  ownerType: 'user' | 'office';
  shared: boolean;
  officeName?: string;
  unlimited: boolean;
  /**
   * Cuánto del saldo son créditos COMPRADOS, que no vencen. `balance` sigue siendo EL TOTAL:
   * esto es una PARTE de él, no una segunda bolsa. Solo para poder decirle al agente, adentro
   * de su wallet, cuánto le queda de lo que pagó.
   */
  purchasedBalance?: number;
  /** Créditos asignados por mes (Imperium=300, Booster=plan). null = ilimitado. */
  monthlyAllowance: number | null;
  /** Consumido en el mes calendario actual. */
  usedThisMonth: number;
  /** Inicio del período (mes calendario) en ISO. */
  periodStart: string;
  recentTransactions: CreditTransactionPreview[];
  /**
   * Solo en un pool compartido (oficina franquicia): el tope mensual que le puso su admin
   * sobre el pool, lo que ya usó, y lo que compró él (que es suyo y no cuenta para el tope).
   * null si cobra de su propio wallet.
   */
  cupoDelAgente: {
    tope: number | null;
    consumidoEsteMes: number;
    disponibleDelPool: number;
    compradoPropio: number;
    frenadoPorTope: boolean;
  } | null;
  /** El admin puede ver el consumo por integrante y ponerle tope a cada uno (Booster, Élite+). */
  repartoDeCreditos?: boolean;
}

export interface WalletOwner {
  name: string;
  subtitle?: string;
  officeId?: string;
  officeName?: string;
  role?: string;
  avatar?: string;
}

export interface WalletListItem {
  _id: string;
  ownerType: 'user' | 'office';
  ownerId: string;
  balance: number;
  totalEarned: number;
  totalConsumed: number;
  lastActivityAt?: string;
  owner: WalletOwner | null;
}

export interface WalletDetail {
  wallet: WalletListItem;
  transactions: CreditTransactionPreview[];
  totalTransactions: number;
}

export interface WalletsFilters {
  ownerType?: 'user' | 'office';
  minBalance?: number;
  maxBalance?: number;
  officeId?: string;
  q?: string;
  includeInactive?: boolean;
}

export interface GrantPayload {
  walletId: string;
  amount: number;
  reason: string;
}

export interface OfficeAgentConsumption {
  userId: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  consumedThisMonth: number;
  countThisMonth: number;
  /** Tope mensual sobre el pool. null = sin tope. */
  topeMensual: number | null;
  llegoAlTope: boolean;
  /** Créditos que compró él: son suyos, no cuentan para el tope. */
  compradoPropio: number;
}

export interface OfficeCreditsResponse {
  office: { _id: string; name: string; city?: string };
  wallet: {
    _id: string;
    balance: number;
    purchasedBalance: number;
    totalEarned: number;
    totalConsumed: number;
    lastActivityAt?: string;
  };
  breakdown: OfficeAgentConsumption[];
  recentTransactions: CreditTransactionPreview[];
}

export interface PricingEntry {
  action: string;
  cost: number;
  active: boolean;
  fromDb: boolean;
  description?: string;
}

export interface PricingUpsertPayload {
  action: string;
  cost: number;
  active?: boolean;
  description?: string;
}

export interface TransactionsQuery {
  walletId?: string;
  ownerType?: 'user' | 'office';
  ownerId?: string;
  kind?: string;
  consumerModule?: string;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  skip?: number;
}

/** Desglose del mes del propio agente — GET /credits/me/breakdown. */
export interface CreditsBreakdown {
  periodo:  { mes: string; diasDelMes: number; diaActual: number };
  total:    number;
  turnos:   number;
  promedio: number;
  porTipo:  Array<{ tipo: string; creditos: number; turnos: number }>;
  porCanal: Array<{ canal: 'web' | 'whatsapp'; creditos: number; turnos: number }>;
  porDia:   Array<{ dia: string; creditos: number }>;
  masCaras: Array<{ fecha: string; creditos: number; canal: string; tools: string[] }>;
  concentracion: { pctTurnos: number; pctCreditos: number } | null;
}

// ─── Paquetes de créditos comprados ───────────────────────────────────────────
// Se compran con plata y NO VENCEN: se pasan de mes a mes y se consumen después de los
// que Imperium regala cada mes. El admin los acredita a mano tras confirmar el ingreso.

export interface DesglosePaquete {
  precio: number;
  creditos: number;
  precioPorCredito: number;
  /** Reservado para pagarle a OpenAI (1 crédito = 1 peso). */
  reservaIa: number;
  ganancia: number;
  /** La ganancia como porcentaje del precio. */
  margenPct: number;
  moneda: string;
}

export interface PaqueteDeCreditos {
  sku: string;
  nombre: string;
  creditos: number;
  precio: number;
  moneda: string;
  ahorroPct?: number;
  desglose: DesglosePaquete;
}

export interface AsignacionDePaquete {
  id: string;
  fecha: string;
  sku: string;
  paquete: string;
  creditos: number;
  precio: number;
  moneda: string;
  reservaIa: number;
  ganancia: number;
  estado: string;
  agente: string;
  agenteId: string | null;
  aprobadoPor: string | null;
  medioDePago: string | null;
  nota: string | null;
}

export interface PedidoDePaquete {
  id: string;
  fecha: string;
  sku: string;
  paquete: string;
  creditos: number;
  precio: number;
  moneda: string;
  ganancia: number;
  nota: string | null;
  /** El comprobante que adjuntó el agente al pedir. Es contra esto que se aprueba. */
  comprobanteUrl: string | null;
  comprobanteTipo: 'imagen' | 'pdf' | null;
  agente: string;
  agenteId: string | null;
  email: string | null;
  /** Quién pide: la foto, y de dónde es. Aprobar es mandarle plata a una persona. */
  avatar: string | null;
  telefono: string | null;
  oficina: string | null;
  /** La inmobiliaria. Con soporte mirando esta pantalla, distingue Imperium de un Booster. */
  tenant: string | null;
}

export interface MiPedidoDePaquete {
  id: string;
  fecha: string;
  paquete: string;
  creditos: number;
  precio: number;
  moneda: string;
  estado: string;
  motivoDelRechazo: string | null;
  comprobanteUrl: string | null;
}

/** A dónde transferir para comprar un paquete. Sin cargar viene en null. */
export interface DatosDeCobro {
  texto: string | null;
  actualizadoEl: string | null;
}

export interface ContabilidadDePaquetes {
  desde: string;
  hasta: string;
  totales: { paquetes: number; creditos: number; vendido: number; reservaIa: number; ganancia: number };
  porPaquete: Array<{ sku: string; nombre: string; paquetes: number; creditos: number; vendido: number; reservaIa: number; ganancia: number }>;
  porAgente: Array<{ agenteId: string; agente: string; paquetes: number; creditos: number; vendido: number; ganancia: number }>;
}

export const creditsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMyCredits: build.query<CreditsMe, void>({
      query: () => '/credits/me',
      transformResponse: (res: { data: CreditsMe }) => res.data,
      providesTags: ['Credits'],
    }),

    getMyTransactions: build.query<{ items: CreditTransactionPreview[]; total: number }, { skip?: number; limit?: number } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.limit) p.set('limit', String(f.limit));
        if (f?.skip)  p.set('skip',  String(f.skip));
        const qs = p.toString();
        return `/credits/me/transactions${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: { items: CreditTransactionPreview[]; total: number } }) => res.data,
      providesTags: ['Credits'],
    }),

    /** En qué se le fueron los créditos del mes al propio agente (tab Créditos). */
    getMyCreditsBreakdown: build.query<CreditsBreakdown, void>({
      query: () => '/credits/me/breakdown',
      transformResponse: (res: { data: CreditsBreakdown }) => res.data,
      providesTags: ['Credits'],
    }),

    listWallets: build.query<WalletListItem[], WalletsFilters | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.ownerType)                  p.set('ownerType',  f.ownerType);
        if (f?.minBalance !== undefined)   p.set('minBalance', String(f.minBalance));
        if (f?.maxBalance !== undefined)   p.set('maxBalance', String(f.maxBalance));
        if (f?.officeId)                   p.set('officeId',   f.officeId);
        if (f?.q)                          p.set('q',          f.q);
        if (f?.includeInactive)            p.set('includeInactive', 'true');
        const qs = p.toString();
        return `/credits/wallets${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: WalletListItem[] }) => res.data,
      providesTags: ['Credits'],
    }),

    getWalletById: build.query<WalletDetail, string>({
      query: (id) => `/credits/wallets/${id}`,
      transformResponse: (res: { data: WalletDetail }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Credits' as const, id }],
    }),

    grantToWallet: build.mutation<{ transactionId: string; balanceAfter: number; amount: number }, GrantPayload>({
      query: ({ walletId, amount, reason }) => ({
        url: `/credits/wallets/${walletId}/grant`,
        method: 'POST',
        body: { amount, reason },
      }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['Credits'],
    }),

    debitFromWallet: build.mutation<{ transactionId: string; balanceAfter: number; amount: number }, GrantPayload>({
      query: ({ walletId, amount, reason }) => ({
        url: `/credits/wallets/${walletId}/debit`,
        method: 'POST',
        body: { amount, reason },
      }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['Credits'],
    }),

    listCreditTransactions: build.query<{ items: CreditTransactionPreview[]; total: number }, TransactionsQuery | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.walletId)       p.set('walletId',       f.walletId);
        if (f?.ownerType)      p.set('ownerType',      f.ownerType);
        if (f?.ownerId)        p.set('ownerId',        f.ownerId);
        if (f?.kind)           p.set('kind',           f.kind);
        if (f?.consumerModule) p.set('consumerModule', f.consumerModule);
        if (f?.action)         p.set('action',         f.action);
        if (f?.from)           p.set('from',           f.from);
        if (f?.to)             p.set('to',             f.to);
        if (f?.limit)          p.set('limit',          String(f.limit));
        if (f?.skip)           p.set('skip',           String(f.skip));
        const qs = p.toString();
        return `/credits/transactions${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: { items: CreditTransactionPreview[]; total: number } }) => res.data,
      providesTags: ['Credits'],
    }),

    getOfficeCredits: build.query<OfficeCreditsResponse, void>({
      query: () => '/credits/office',
      transformResponse: (res: { data: OfficeCreditsResponse }) => res.data,
      providesTags: ['Credits'],
    }),

    /** Tope mensual de un agente de la oficina sobre el pool compartido. null = sin tope. */
    setTopeDeAgente: build.mutation<{ userId: string; topeMensual: number | null }, { userId: string; tope: number | null }>({
      query: ({ userId, tope }) => ({
        url: `/credits/office/agentes/${userId}/tope`,
        method: 'PUT',
        body: { tope },
      }),
      transformResponse: (res: { data: { userId: string; topeMensual: number | null } }) => res.data,
      invalidatesTags: ['Credits'],
    }),

    listPricing: build.query<PricingEntry[], void>({
      query: () => '/credits/pricing',
      transformResponse: (res: { data: PricingEntry[] }) => res.data,
      providesTags: ['Credits'],
    }),

    upsertPricing: build.mutation<PricingEntry, PricingUpsertPayload>({
      query: (body) => ({ url: '/credits/pricing', method: 'PUT', body }),
      transformResponse: (res: { data: PricingEntry }) => res.data,
      invalidatesTags: ['Credits'],
    }),

    // ── Paquetes de créditos ──────────────────────────────────────────────
    listarPaquetes: build.query<PaqueteDeCreditos[], void>({
      query: () => ({ url: '/credits/paquetes' }),
      transformResponse: (res: { data: { paquetes: PaqueteDeCreditos[] } }) => res.data.paquetes,
      providesTags: ['Credits'],
    }),
    /** Sube el comprobante y devuelve su URL, para adjuntarla al acreditar. Ruta de admin. */
    subirComprobante: build.mutation<{ url: string; tipo: 'imagen' | 'pdf' }, FormData>({
      query: (formData) => ({ url: '/credits/paquetes/comprobante', method: 'POST', body: formData }),
      transformResponse: (res: { data: { url: string; tipo: 'imagen' | 'pdf' } }) => res.data,
    }),
    /** El mismo archivo, subido por el AGENTE antes de pedir el paquete. */
    subirMiComprobante: build.mutation<{ url: string; tipo: 'imagen' | 'pdf' }, FormData>({
      query: (formData) => ({ url: '/credits/me/paquetes/comprobante', method: 'POST', body: formData }),
      transformResponse: (res: { data: { url: string; tipo: 'imagen' | 'pdf' } }) => res.data,
    }),
    asignarPaquete: build.mutation<
      { ordenId: string; yaEstaba: boolean; balance: number | null; purchasedBalance: number | null },
      { userId: string; sku: string; idempotencyKey: string; medioDePago?: string; ingresadoEl?: string; nota?: string; comprobanteUrl?: string; comprobanteTipo?: 'imagen' | 'pdf' }
    >({
      query: (body) => ({ url: '/credits/paquetes/asignar', method: 'POST', body }),
      transformResponse: (res: { data: { ordenId: string; yaEstaba: boolean; balance: number | null; purchasedBalance: number | null } }) => res.data,
      invalidatesTags: ['Credits'],
    }),
    listarAsignaciones: build.query<AsignacionDePaquete[], { userId?: string; desde?: string; hasta?: string; limit?: number } | void>({
      query: (params) => ({ url: '/credits/paquetes/asignaciones', params: params ?? undefined }),
      transformResponse: (res: { data: { items: AsignacionDePaquete[] } }) => res.data.items,
      providesTags: ['Credits'],
    }),
    contabilidadDePaquetes: build.query<ContabilidadDePaquetes, { desde?: string; hasta?: string } | void>({
      query: (params) => ({ url: '/credits/paquetes/contabilidad', params: params ?? undefined }),
      transformResponse: (res: { data: ContabilidadDePaquetes }) => res.data,
      providesTags: ['Credits'],
    }),

    // Pedidos: el agente pide, el admin confirma que la plata entró.
    pedirPaquete: build.mutation<
      { ordenId: string; estado: string },
      { sku: string; nota?: string; idempotencyKey: string; comprobanteUrl: string; comprobanteTipo: 'imagen' | 'pdf' }
    >({
      query: (body) => ({ url: '/credits/me/paquetes', method: 'POST', body }),
      transformResponse: (res: { data: { ordenId: string; estado: string } }) => res.data,
      invalidatesTags: ['Credits'],
    }),
    misPedidosDePaquetes: build.query<MiPedidoDePaquete[], void>({
      query: () => ({ url: '/credits/me/paquetes' }),
      transformResponse: (res: { data: { items: MiPedidoDePaquete[] } }) => res.data.items,
      providesTags: ['Credits'],
    }),
    pedidosPendientes: build.query<PedidoDePaquete[], void>({
      query: () => ({ url: '/credits/paquetes/pendientes' }),
      transformResponse: (res: { data: { items: PedidoDePaquete[] } }) => res.data.items,
      providesTags: ['Credits'],
    }),
    aprobarPedidoDePaquete: build.mutation<{ ordenId: string; yaEstaba: boolean }, { id: string; medioDePago?: string; ingresadoEl?: string; nota?: string; comprobanteUrl?: string; comprobanteTipo?: 'imagen' | 'pdf' }>({
      query: ({ id, ...body }) => ({ url: `/credits/paquetes/pedidos/${id}/aprobar`, method: 'PUT', body }),
      transformResponse: (res: { data: { ordenId: string; yaEstaba: boolean } }) => res.data,
      invalidatesTags: ['Credits'],
    }),
    rechazarPedidoDePaquete: build.mutation<{ ok: boolean }, { id: string; motivo: string }>({
      query: ({ id, ...body }) => ({ url: `/credits/paquetes/pedidos/${id}/rechazar`, method: 'PUT', body }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: ['Credits'],
    }),

    // ── A dónde transferir ────────────────────────────────────────────────
    datosDeCobro: build.query<DatosDeCobro, void>({
      query: () => ({ url: '/credits/me/paquetes/datos-de-cobro' }),
      transformResponse: (res: { data: DatosDeCobro }) => res.data,
      providesTags: ['DatosDeCobro'],
    }),
    guardarDatosDeCobro: build.mutation<DatosDeCobro, { texto?: string | null }>({
      query: (body) => ({ url: '/credits/paquetes/datos-de-cobro', method: 'PUT', body }),
      transformResponse: (res: { data: DatosDeCobro }) => res.data,
      invalidatesTags: ['DatosDeCobro'],
    }),
  }),
});

export const {
  useGetMyCreditsQuery,
  useGetMyTransactionsQuery,
  useGetMyCreditsBreakdownQuery,
  useSetTopeDeAgenteMutation,
  useListWalletsQuery,
  useGetWalletByIdQuery,
  useGrantToWalletMutation,
  useDebitFromWalletMutation,
  useListCreditTransactionsQuery,
  useGetOfficeCreditsQuery,
  useListPricingQuery,
  useUpsertPricingMutation,
  useListarPaquetesQuery,
  useAsignarPaqueteMutation,
  useSubirComprobanteMutation,
  useListarAsignacionesQuery,
  useContabilidadDePaquetesQuery,
  usePedirPaqueteMutation,
  useSubirMiComprobanteMutation,
  useDatosDeCobroQuery,
  useGuardarDatosDeCobroMutation,
  useMisPedidosDePaquetesQuery,
  usePedidosPendientesQuery,
  useAprobarPedidoDePaqueteMutation,
  useRechazarPedidoDePaqueteMutation,
} = creditsApi;
